import type { MouseEvent, ReactNode } from 'react';
import styles from './Chip.module.css';
import Icon from './Icon';
import type { IconName } from './icons';

export type ChipTone = 'neutral' | 'accent' | 'success' | 'danger';
export type ChipSize = 'sm' | 'md';

export interface ChipProps {
  children: ReactNode;
  tone?: ChipTone;
  size?: ChipSize;
  icon?: IconName;
  /** Renders an anchor. External links get the arrow glyph and the rel pair
      automatically, so no call site has to remember `noopener`. */
  href?: string;
  external?: boolean;
  onClick?: () => void;
  /** Adds a dismiss control. Its click is stopped from reaching the chip, so a
      chip that is both a link and removable behaves the way it looks. */
  onRemove?: () => void;
  removeLabel?: string;
  selected?: boolean;
  title?: string;
  className?: string;
}

const ICON_SIZE: Record<ChipSize, number> = { sm: 11, md: 13 };

/**
 * A compact pill for a fragment of content: a cited source, an attached file, a
 * filter, a status.
 *
 * A chip is not a button. If the thing is a command ("Save", "Retry"), use
 * `Button`; chips are for content the user can act on, which is why they carry
 * a tone rather than a variant.
 */
export default function Chip({
  children,
  tone = 'neutral',
  size = 'md',
  icon,
  href,
  external = false,
  onClick,
  onRemove,
  removeLabel,
  selected = false,
  title,
  className,
}: ChipProps) {
  const classes = [
    styles.chip,
    styles[tone],
    styles[size],
    selected ? styles.selected : '',
    href || onClick ? styles.clickable : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const inner = (
    <>
      {icon ? <Icon name={icon} size={ICON_SIZE[size]} /> : null}
      <span className={styles.label}>{children}</span>
      {external ? <Icon name="arrow-external" size={ICON_SIZE[size]} /> : null}
      {onRemove ? (
        <button
          type="button"
          className={styles.remove}
          aria-label={removeLabel ?? `Remove ${typeof children === 'string' ? children : 'item'}`}
          onClick={(e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
        >
          <Icon name="close" size={9} />
        </button>
      ) : null}
    </>
  );

  if (href) {
    return (
      <a
        className={classes}
        href={href}
        title={title}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
      >
        {inner}
      </a>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        className={classes}
        title={title}
        aria-pressed={selected || undefined}
        onClick={onClick}
      >
        {inner}
      </button>
    );
  }

  return (
    <span className={classes} title={title}>
      {inner}
    </span>
  );
}
